import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import getUserType from '$utils/getUserType';
import { UserTypes } from '$types/UserTypes';
import ActiveValues from '$constants/ActiveValues';
import StatusCandidatePosition from '$types/serverTypes/StatusCandidatePosition';

interface Props {
  value: ActiveValues;
  candidatesCount: Partial<Record<ActiveValues, number>>;
  showInterestedToEmployer: boolean;
}

const SPKPositionStatusChip = ({
  statusName,
  statusAmmount = 0,
  onClick,
  isActive = false,
}: {
  statusName: string;
  statusAmmount?: number;
  onClick: () => void;
  isActive: boolean;
}) => {
  return (
    <span
      onClick={() => onClick()}
      aria-hidden
      className={`rounded-full flex items-center self-center px-4 py-1 hover:cursor-pointer ${
        isActive
          ? 'bg-spk-highContrast text-spk-softBlue'
          : 'bg-employer-secondary'
      }`}
      data-testid="SPKPositionStatusChip"
    >
      {statusName}
      {statusAmmount !== 0 && <span>({statusAmmount})</span>}
    </span>
  );
};

SPKPositionStatusChip.defaultProps = {
  statusAmmount: 0,
};

export const SPKPositionStatusSwitch: FC<Props> = ({
  value,
  candidatesCount,
  showInterestedToEmployer = false,
}) => {
  const [searchParams, setSeachParams] = useSearchParams();
  const userRole = getUserType();

  const [t] = useTranslation('global');

  const changeHandler = (positionStatus: ActiveValues) => {
    const paramsCopy = new URLSearchParams(searchParams);
    paramsCopy.set('cps', positionStatus);
    setSeachParams(paramsCopy);
  };

  const PositionStatusChips = useMemo(() => {
    return [
      {
        statusName: t('ChipGroup.New'),
        statusAmmount: candidatesCount[StatusCandidatePosition.new],
        activeValue: ActiveValues.New,
      },
      {
        statusName: t('ChipGroup.ShortList'),
        statusAmmount: candidatesCount[StatusCandidatePosition.shortlisted],
        activeValue: ActiveValues.ShortList,
      },
      {
        statusName: t('ChipGroup.Interested'),
        statusAmmount: candidatesCount[StatusCandidatePosition.interested],
        activeValue: ActiveValues.interested,
        shouldHide:
          (userRole === UserTypes.EMPLOYER &&
            showInterestedToEmployer === false) ||
          (userRole !== UserTypes.INTERNAL && userRole !== UserTypes.EMPLOYER),
      },
      {
        statusName: t('ChipGroup.Inactive'),
        statusAmmount: candidatesCount[StatusCandidatePosition.declined],
        activeValue: ActiveValues.declined,
        shouldHide: userRole !== UserTypes.INTERNAL,
      },
      {
        statusName: t('ChipGroup.BeingInterviewed'),
        statusAmmount:
          candidatesCount[StatusCandidatePosition.requestInterview],
        activeValue: ActiveValues.requestInterview,
      },
      {
        statusName: t('ChipGroup.Hired'),
        statusAmmount: candidatesCount[StatusCandidatePosition.hired],
        activeValue: ActiveValues.hired,
        shouldHide: userRole !== UserTypes.INTERNAL,
      },
      {
        statusName: t('ChipGroup.OfferSent'),
        statusAmmount: candidatesCount[StatusCandidatePosition.offerSent],
        activeValue: ActiveValues.OfferSent,
      },
      {
        statusName: t('ChipGroup.Find'),
        activeValue: ActiveValues.find,
        shouldHide: userRole !== UserTypes.INTERNAL,
      },
    ];
  }, [candidatesCount, showInterestedToEmployer, t, userRole]);

  return (
    <div className="flex flex-row gap-2 max-w-full overflow-x-auto items-center">
      {PositionStatusChips.map((singleChip) => {
        if (singleChip.shouldHide) return null;

        return (
          <SPKPositionStatusChip
            key={`SPKPositionStatusChip_${singleChip.activeValue}`}
            statusName={singleChip.statusName}
            statusAmmount={singleChip.statusAmmount}
            onClick={() => changeHandler(singleChip.activeValue)}
            isActive={value === singleChip.activeValue}
          />
        );
      })}
    </div>
  );
};
